import type { MouseEventHandler } from 'react';

import { FileText } from '@phosphor-icons/react';
import classNames from 'classnames';

import CurrencyInput from '../../ui/CurrencyInput';
import DiscordIcon from '../../ui/icons/Discord';

import type { Price, PricingFormMode, Tier } from '../../types';
import * as styles from '../Pricing.css';
import type { TPricingAttributes } from '../Pricing.types';
import ExternalCheckout from './ExternalCheckout';
import { FeatureItem } from './FeatureItem';
import { PriceFigures } from './PriceFigures';
import { RecommendedBadge } from './RecommendedBadge';
import { renderExpiresInXDays } from './renderExpiresInXDays';
import { Tab } from './Tab';
import { Tabs } from './Tabs';

interface Props {
  attributes: TPricingAttributes;
  tier: Tier;
  isInternalCheckout: boolean;
  selectedPrice?: Price;
  donationAmountCents: number;
  setDonationAmountCents: (value: number) => void;
  isCtaDisabled: boolean;
  onClickPriceTab: (price: Price) => void;
  isSelected: boolean;
  currentFormMode?: PricingFormMode;
  hasDiscordAccess?: boolean;
  translations?: Record<string, string>;
}

const PriceBlock = ({
  attributes,
  tier,
  isInternalCheckout,
  selectedPrice,
  donationAmountCents,
  setDonationAmountCents,
  isCtaDisabled,
  onClickPriceTab,
  isSelected,
  currentFormMode,
  hasDiscordAccess,
  translations,
}: Props) => {
  const shouldShowRecommendedBadge =
    attributes?.recommendedObjectType === 'price' && attributes?.recommendedPriceId === selectedPrice?.id;

  const features = selectedPrice?.features || [];
  const hasFeatures = features.length > 0;
  const shouldShowFeatureDivider = hasFeatures || hasDiscordAccess;

  const isGroupMode = currentFormMode === 'group';
  const renderDiscountPriceInline = !!attributes?.renderDiscountPriceInline;

  const premiumOfferDetails = isGroupMode
    ? []
    : [
        selectedPrice?.premium_offer?.custom_terms ||
          (selectedPrice?.premium_offer?.offer_type !== 'paid_trial' && selectedPrice?.premium_offer?.terms),
        selectedPrice?.premium_offer?.description,
        selectedPrice?.premium_offer?.expires_at && renderExpiresInXDays(selectedPrice?.premium_offer?.expires_at),
        // Strings only: a malformed `terms` (e.g. an unresolved i18n plural hash) must
        // never reach JSX as an object and white-screen the whole card.
      ].filter((detail): detail is string => typeof detail === 'string' && detail.length > 0);
  const hasPremiumOfferDetails = premiumOfferDetails.length > 0;

  const handleClickPriceTab: (price: Price) => MouseEventHandler<HTMLButtonElement> = (price: Price) => (e) => {
    e.stopPropagation();
    onClickPriceTab(price);
  };

  const isFreePlan = selectedPrice?.id === 'free';
  const externalCheckoutCTAText = (isFreePlan ? tier.cta : selectedPrice?.cta) || undefined;

  return (
    <>
      {shouldShowRecommendedBadge && <RecommendedBadge text={attributes?.recommendedBadgeText || 'Recommended'} />}
      <div className="flex flex-col justify-between w-full h-full gap-4">
        <div className="flex flex-col gap-y-4">
          <div className="flex flex-col gap-y-4">
            <h1
              className={classNames(
                'text-xl font-semibold text-left m-0 p-0 w-full leading-none',
                isSelected ? styles.tierNameSelected : styles.tierName,
              )}
            >
              {tier.name}
            </h1>
            <PriceFigures
              selectedPrice={selectedPrice}
              tier={tier}
              currentFormMode={currentFormMode}
              isInternalCheckout={isInternalCheckout}
              showWeeklyPricing={!!attributes?.showWeeklyPricing}
              translations={translations}
              inlineDiscount={renderDiscountPriceInline}
              donationSlot={
                <CurrencyInput
                  name={`donation-amount-${selectedPrice?.id}`}
                  value={donationAmountCents}
                  onChange={(value: number) => setDonationAmountCents(value)}
                  minCents={100}
                  maxCents={100000}
                  currency={selectedPrice?.currency}
                  className="w-full"
                  required
                />
              }
            />
          </div>

          {tier.prices.length > 1 ? (
            <Tabs>
              {tier.prices.map((price) => {
                const isSelected = price.id === selectedPrice?.id;
                const tabLabel = price.denominator || price.interval.split('_').join(' ');

                return (
                  <Tab key={price.id} isSelected={isSelected} label={tabLabel} onClick={handleClickPriceTab(price)} />
                );
              })}
            </Tabs>
          ) : null}

          <p
            className={classNames(
              'w-full leading-normal text-sm font-normal text-left',
              isSelected ? styles.tierDescriptionSelected : styles.tierDescription,
            )}
          >
            {tier.description}
          </p>

          {hasPremiumOfferDetails && (
            <ul className="flex flex-col gap-2">
              {premiumOfferDetails.map((detail, index) => (
                <li key={index} className="flex flex-row gap-3 items-start text-left">
                  <div className="shrink-0">
                    <FileText size={20} className={classNames(styles.tierOfferDetailsIconColor)} />
                  </div>
                  <span className={classNames('text-sm leading-normal font-normal', styles.tierOfferDetailsTextColor)}>
                    {detail}
                  </span>
                </li>
              ))}
            </ul>
          )}

          {(hasFeatures || hasDiscordAccess) && (
            <div className="flex flex-col gap-4">
              {shouldShowFeatureDivider && <hr className="border-t" />}
              <div className="flex flex-col gap-2">
                {hasFeatures && (
                  <ul className="flex flex-col gap-2">
                    {features.map((feature, index) => (
                      <li key={index} className="flex flex-row gap-3 items-start text-left">
                        <FeatureItem feature={feature} isSelected={isSelected} />
                      </li>
                    ))}
                  </ul>
                )}
                {hasDiscordAccess && (
                  <div className="flex flex-row gap-3 items-center text-left">
                    <DiscordIcon aria-hidden className="h-4 w-4 shrink-0" />
                    <span
                      className={classNames(
                        'text-sm leading-normal font-normal',
                        isSelected ? styles.tierFeatureTextColorSelected : styles.tierFeatureTextColor,
                      )}
                    >
                      Discord access included
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
        {!isInternalCheckout && currentFormMode === 'individual' && (
          <ExternalCheckout
            borderRadius={attributes?.buttonBorderRadius?.toString() ?? ''}
            ctaText={externalCheckoutCTAText}
            isFreePlan={isFreePlan}
            isCtaDisabled={isCtaDisabled}
          />
        )}
      </div>
    </>
  );
};

export default PriceBlock;
